import { Button } from 'atoms/Button';
import {
  ContentsHandler,
  ContentsHandlerContext,
  Layout,
  Types,
} from 'commons/ui-generator/ContentsHandler';
import { useGetApplyForm } from 'hooks/u002/useGetApplyFormHooks';
import LoadingModal from 'organisms/LoadingModal';
import { FC, useCallback, useContext, useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppFooter } from 'templates/AppFooter';
import { AppLogoHeader } from 'templates/AppLogoHeader';
import { PageContent } from 'templates/PageContent';
import { A34 } from 'api/U002';
import { useTranslationWithLang } from 'hooks/useTranslationWithLang';
import { AppContext } from 'templates/AppContextProvider';
import AuditionFormLayout from 'organisms/u002/AuditionFormLayout';
import {
  createPrefectures,
  createPreferredVenueList,
  filterInputableApplyForms,
  getImageSrcGlobal,
} from 'commons/auditionForm/utils';
import { useTemplateMembersNum } from 'hooks/u002/useTemplateMembersNumHooks';
import { useGetAudition } from 'hooks/u002/useGetAuditionHooks';
import { useAuth } from 'hooks/useAuth';
import { useAuditionForm, AuditionFormValuesSchemaInput as Form } from 'hooks/u002/useAuditionForm';
import { SubmitHandler } from 'react-hook-form';
import { AuditionFormRouteContext } from 'pages/AuditionFormTop';
import ScrollTop from 'organisms/u002/audition-form/ScrollTop';

const title = 'preview';
type AuditionFormSampleProps = unknown;
/* 実際に応募者側のロジックで生成された応募フォーム(アンケート)を確認できる機能 */
export const AuditionFormSample: FC<AuditionFormSampleProps> = () => {
  const { t: tAuditionForm } = useTranslationWithLang('auditionForm');
  const { t: tContentsHandler } = useTranslationWithLang('contentsHandler');
  const { currentLang, isGlobal } = useContext(AppContext);
  const { audition_id, examination_no } = useContext(AuditionFormRouteContext);
  const [searchParam] = useSearchParams();
  const representative = searchParam.get('representative');
  const isJapanese = currentLang === 'ja';
  const auth = useAuth();
  //フォームの内容を取得
  const [getApplyFormData, isLoading, err] = useGetApplyForm({
    audition_id,
    examination_no,
    lang: currentLang,
  });
  // オーディションタイトル用にオーディション情報取得
  const [data, isDataLoading, err_a] = useGetAudition({
    audition_id,
    examination_no,
    lang: currentLang,
    representative_user: representative ?? undefined,
  });
  // ApplyFormをApplyFormとLayoutConfigsに分割
  const { applyForms, layoutConfigs } = useMemo(
    () =>
      Layout.transformApplyFormAndLayoutConfigs(
        //３−２．海外ドメインの場合、format=20(同意書)、11(郵便番号)を表示しない。
        (isGlobal
          ? getApplyFormData?.apply_form.filter(
              (form) => form.format !== '20' && form.format !== '11'
            )
          : getApplyFormData?.apply_form
        )?.filter((form) => {
          //３−３．言語が日本語以外の場合、カナ入力はできないため、format=51(参加者名カナ)を表示しない。
          if (!isJapanese && form.format === '51') return false;
          return true;
        }) ?? []
      ),
    [isGlobal, isJapanese, getApplyFormData]
  );
  // 入力可能な項目のApplyForm
  const inputableApplyForms = useMemo(() => filterInputableApplyForms(applyForms), [applyForms]);
  const prefectures = useMemo(
    () => createPrefectures(getApplyFormData, isJapanese),
    [getApplyFormData, isJapanese]
  );
  const preferred_venue_list = useMemo(
    () => createPreferredVenueList(getApplyFormData, isJapanese),
    [getApplyFormData, isJapanese]
  );

  const { register, handleSubmit, setValue, getValues, watch } = useAuditionForm({
    applyForms: inputableApplyForms,
    prefectures,
    preferred_venue_list,
  });

  const {
    applyType,
    templateMembersIds,
    setTemplateMembersIds,
    onChangeApplyType,
    onChangeTemplateMembersNum,
    onClickTemplateMembersDelete,
  } = useTemplateMembersNum(audition_id, examination_no, setValue, getValues, inputableApplyForms);

  // 言語コードを設定
  ContentsHandler.langCode = ((applyForms.at(0)?.lang as `${number}`) || undefined) ?? '0';

  // アーティスト情報：アカウント登録時氏名と同じ
  const onChangeArtistSameAsAccount = useCallback<
    NonNullable<Types.ContentsHandlerContextValue['onChangeArtistSameAsAccount']>
  >(
    (e, apply_form_no) => {
      const { checked } = e.target;
      const { name, kana } = apply_form_no;
      const { firstname, lastname, firstname_kana, lastname_kana } =
        getApplyFormData?.userinfo[0] ?? {};
      if (checked) {
        const nameValue = (isJapanese ? [lastname, firstname] : [firstname, lastname])
          .filter(Boolean)
          .join(' ');
        setValue(`${name}`, nameValue);
        if (isJapanese) {
          const kanaValue = [lastname_kana, firstname_kana].filter(Boolean).join('　');
          setValue(`${kana}`, kanaValue);
        }
      } else {
        setValue(`${name}`, '');
        if (isJapanese) {
          setValue(`${kana}`, '');
        }
      }
    },
    [getApplyFormData, isJapanese, setValue]
  );

  // 住所：郵便番号検索
  const onClickTemplateAddressSearch = useCallback<
    NonNullable<Types.ContentsHandlerContextValue['onClickTemplateAddressSearch']>
  >(
    async (e, { postcode, prefecture, city }) => {
      e.preventDefault();
      if (postcode == null) return;
      const postcodeValue = getValues(`${postcode}` as const);
      if (typeof postcodeValue !== 'string') return;
      const result = await A34({
        post_code: postcodeValue,
        user_id: auth.username,
      }).then((res) => res.post_list.at(0));
      if (result == null) return;
      if (prefecture != null) {
        const value = result.prefectures_kbn;
        setValue(`${prefecture}`, value);
      }
      if (city != null) {
        setValue(`${city}`, result.city);
      }
    },
    [setValue, getValues, auth.username]
  );

  //メンバー情報
  const [hasErrorInMemberConsent, setHasErrorInMemberConsent] = useState<boolean>(false);

  // 選択肢
  const [isMoreSelected, setIsMoreSelected] = useState<string[]>([]);
  const onChangeCheckParts = useCallback<
    NonNullable<Types.ContentsHandlerContextValue['onChangeCheckParts']>
  >((e, selectable, name, required) => {
    let checked = 0;
    const checkBoxes = document.getElementsByName(`${name}`) as NodeListOf<HTMLInputElement>;
    checkBoxes.forEach((checkBox) => {
      if (checkBox.checked) {
        checked++;
      }
    });
    if (required) {
      checkBoxes.forEach((checkbox) => {
        const noChecked = checked === 0;
        checkbox.required = noChecked;
      });
    }
    if (selectable < 1) return;
    if (checked > selectable) {
      setIsMoreSelected([...isMoreSelected, name]);
      e.currentTarget.checked = false;
    } else {
      setIsMoreSelected((prev) => prev.filter((el) => el !== name));
    }
  }, []);

  // 生年月日パーツ
  const [hasErrorInSelectDate, setHasErrorInSelectDate] = useState<boolean>(false);

  const onChangeSelectDateParts = useCallback<
    NonNullable<Types.ContentsHandlerContextValue['onChangeSelectDateParts']>
  >((e, apply_form_no, required) => {
    const targetSelectDate = Array.from(
      document.querySelectorAll<HTMLSelectElement>('[data-selectdate]')
    ).filter((element) => element.dataset.selectDate?.includes(`${apply_form_no}`));

    let hasSelected = targetSelectDate.map((selectDate) => selectDate.value !== '');
    if (!hasSelected.includes(true) && !required) {
      targetSelectDate.forEach((selectDate) => {
        if (selectDate.required) selectDate.required = false;
      });
    } else {
      targetSelectDate.forEach((selectDate) => {
        if (selectDate.required) selectDate.required = true;
      });
    }
  }, []);

  const watchedValues = watch();

  useEffect(() => {
    if (isLoading) return;
    Object.entries(watchedValues).forEach(([groupNumber, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'labelAndValues' in value &&
        Array.isArray(value.labelAndValues)
      ) {
        if (!value?.labelAndValues) return;

        const labelAndValues = value.labelAndValues;
        const checkedCount = labelAndValues.filter((v) => v !== '').length;
        const isRequired = checkedCount === 0;

        const checkboxes = document.getElementsByName(
          `${groupNumber}.labelAndValues`
        ) as NodeListOf<HTMLInputElement>;
        checkboxes.forEach((cb) => {
          if (cb.classList.contains('hasRequired')) cb.required = isRequired;
        });
      }
    });
  }, [isLoading, watchedValues]);

  useEffect(() => {
    if (isLoading) return;
    Object.entries(watchedValues).forEach(([groupNumber, value]) => {
      if (value && typeof value === 'object' && Array.isArray(value)) {
        if (!value) return;

        const targetSelectDate = Array.from(
          document.querySelectorAll<HTMLSelectElement>('[data-selectdate]')
        ).filter((element) => element.dataset.selectdate?.includes(`${groupNumber}`));

        const hasSelected = targetSelectDate.map((selectDate) => selectDate.value !== '');

        targetSelectDate.forEach((selectDate) => {
          if (
            (selectDate.value === '' && hasSelected.includes(true)) ||
            selectDate.classList.contains('hasRequired')
          ) {
            selectDate.required = true;
          }
        });
      }
    });
  }, [isLoading, watchedValues]);

  // 希望会場パーツ
  const onChangePreferredVenueSelect = useCallback<
    NonNullable<Types.ContentsHandlerContextValue['onChangePreferredVenueSelect']>
  >(
    (e) => {
      const targetCheckBoxs = document.querySelectorAll<HTMLInputElement>(
        `input[type="checkbox"][id*="${CSS.escape('possibilityVenue')}"]`
      );
      if (!targetCheckBoxs) return;

      const targetCbsName = targetCheckBoxs[0].name as `${number}.labelAndValues`;
      const checkedValues = watch(targetCbsName) as string[];
      targetCheckBoxs.forEach((checkbox) => {
        if (checkbox.value == e.currentTarget.value) {
          if (typeof checkedValues === 'object' && checkedValues.length > 0) {
            setValue(
              targetCbsName,
              checkedValues.filter((val) => val != e.currentTarget.value)
            );
          }
          checkbox.checked = false;
          checkbox.parentElement!.style.opacity = '0.5 !important';
          checkbox.disabled = true;
        } else {
          checkbox.parentElement!.style.opacity = '1.0 !important';
          checkbox.disabled = false;
        }
      });
    },
    [watchedValues]
  );
  // 希望会場パーツ：画面読み込み時
  useEffect(() => {
    if (isLoading) return;
    const targetSelect = document.querySelector<HTMLSelectElement>(
      `select[id*="${CSS.escape('preferredVenue')}"]`
    );
    // 希望会場の選択肢がある且つ、選択値が空の場合、選択値として初期値の「1」を設定
    const targetSelectName = targetSelect?.name as `${number}`;
    const targetSelectValue = watch(targetSelectName);
    if (
      targetSelect &&
      targetSelect?.options.length > 0 &&
      (!targetSelectValue || targetSelectValue === '')
    ) {
      setValue(targetSelectName, '1');
      targetSelect.value = '1';
    }
    // 参加可能会場の選択肢の活性・非活性制御
    const targetCheckBoxs = document.querySelectorAll<HTMLInputElement>(
      `input[type="checkbox"][id*="${CSS.escape('possibilityVenue')}"]`
    );
    if (!targetSelect || !targetCheckBoxs) return;
    targetCheckBoxs.forEach((checkbox) => {
      if (checkbox.value == targetSelect.value) {
        checkbox.checked = false;
        checkbox.parentElement!.style.opacity = '0.5';
        checkbox.disabled = true;
      } else {
        checkbox.parentElement!.style.opacity = '1.0';
        checkbox.disabled = false;
      }
    });
  }, [isLoading, watchedValues]);

  // 同意書
  const [hasErrorConsentFiles, setHasErrorConsentFiles] = useState<boolean>(false);

  // TODO ToItemListOptionsを使わずContentsHandlerContextを使って処理を書き換える
  const items = useMemo(
    () =>
      ContentsHandler.toItemList(applyForms, {
        'template:address': {
          prefectures,
        },
        'template:venue': {
          preferred_venue_list,
        },
        image: isGlobal
          ? {
              getSrc: getImageSrcGlobal,
            }
          : undefined,
        isGlobal,
      }),
    [applyForms, prefectures, preferred_venue_list, isGlobal]
  );
  console.debug(items);

  const formElements = useMemo(
    () => (
      <>
        {items.map((item) => (
          <div key={item.uuid} className="p-2">
            {ContentsHandler.toElement(item)}
          </div>
        ))}
      </>
    ),
    [items]
  );

  if (isLoading || isDataLoading) {
    return <LoadingModal />;
  }
  if (err) {
    let message = '';
    if (err instanceof Error) {
      message =
        err.message === 'MU002A0103E'
          ? tAuditionForm('TU002_063')
          : err.message === 'MU002A0104E'
            ? tAuditionForm('TU002_064')
            : tAuditionForm('TU002_050');
    }
    return (
      <>
        <AppLogoHeader />
        <PageContent>
          <h1 className=" text-xl mb-16 text-center">{title}</h1>
          <div className="text-[--c-error]">{message}</div>
        </PageContent>
        <AppFooter />
      </>
    );
  }

  const onSubmit: SubmitHandler<Form> = async (values) => {
    console.debug('onSubmit', values);
  };

  if (getApplyFormData == null) {
    return <></>;
  }

  console.debug(getApplyFormData);
  return (
    <>
      <ContentsHandlerContext.Provider
        value={{
          t: tContentsHandler,
          onChangeArtistSameAsAccount,
          onClickTemplateAddressSearch,
          applyType,
          templateMembersIds,
          setTemplateMembersIds,
          onChangeApplyType,
          onChangeTemplateMembersNum,
          onClickTemplateMembersDelete,
          register,
          isMoreSelected,
          onChangeCheckParts,
          onChangeSelectDateParts,
          hasErrorInSelectDate,
          hasErrorInMemberConsent,
          hasErrorConsentFiles,
          onChangePreferredVenueSelect,
        }}
      >
        <AuditionFormLayout layoutConfigs={layoutConfigs}>
          <h1 className="text-xl py-8 text-center">{data?.title}</h1>
          <form onSubmit={handleSubmit(onSubmit, console.error)}>
            {formElements}
            <div className={'flex justify-center items-center py-8'}>
              <Button className="w-[10rem]" label={'preview'} type="submit" />
            </div>
          </form>
        </AuditionFormLayout>
        <ScrollTop footerHeight={layoutConfigs.footer?.height} />
      </ContentsHandlerContext.Provider>
    </>
  );
};
